import { z } from 'zod'
import { salesLineGroupSchema } from '../enums/sales-packet.enum.js'
import { MAX_SCHEDULE_PAYMENTS, scheduleDueInputSchema, scheduleEntryInputSchema } from '../helpers/sales-schedule.js'
import { calendarDaySchema } from '../validators/temporal.validator.js'
import { timeOfDaySchema } from '../validators/time-of-day.validator.js'

/**
 * A packet as staff send it (docs/sales.spec.md, "Sending a packet"): the date
 * and window, what's offered, the contract, the schedule, who signs and who
 * countersigns, and until when. Every price is as staff left it; the price list
 * only starts them.
 */

export const packetLineInputSchema = z
  .object({
    group: salesLineGroupSchema,
    /** The price list item it started from, if any. */
    itemId: z.string().uuid().nullable(),
    name: z.string().trim().min(1, 'Give every line a name.').max(120, 'That line’s name is too long.'),
    description: z.string().max(2000, 'That description is too long.'),
    unitCents: z.number().int().min(0, 'A price can’t be negative.').max(100_000_000, 'That price is too high.'),
    quantity: z.number().int().min(1, 'A quantity is at least 1.').max(10_000, 'That quantity is too high.'),
    taxable: z.boolean(),
    serviceCharged: z.boolean(),
  })
  .strict()
export type PacketLineInput = z.infer<typeof packetLineInputSchema>

/** What a proposal and an amendment both say: when, what's offered, the contract and how it's paid. */
const proposalFields = {
  date: calendarDaySchema,
  /** Times at the venue, both or neither; neither is the venue's usual window. An end before the start is the next morning. */
  startsAt: timeOfDaySchema.nullable(),
  endsAt: timeOfDaySchema.nullable(),
  lines: z.array(packetLineInputSchema).min(1, 'Offer at least one thing.').max(100, 'That’s too many lines.'),
  contractTemplateId: z.string().uuid(),
  schedule: z.array(scheduleEntryInputSchema).min(1).max(MAX_SCHEDULE_PAYMENTS),
  securityDeposit: z
    .object({ cents: z.number().int().min(1).max(100_000_000), due: scheduleDueInputSchema })
    .strict()
    .nullable(),
  countersignerId: z.string().uuid(),
  validUntil: calendarDaySchema,
}

export const createPacketInputSchema = z
  .object({
    ...proposalFields,
    signerUserIds: z.array(z.string().uuid()).min(1, 'Choose who signs.').max(6, 'At most six people can sign.'),
  })
  .strict()
  .refine((p) => (p.startsAt === null) === (p.endsAt === null), { message: 'Give both times, or neither.', path: ['endsAt'] })
  .refine((p) => new Set(p.signerUserIds).size === p.signerUserIds.length, { message: 'Each person signs once.', path: ['signerUserIds'] })
  .refine((p) => p.validUntil <= p.date, { message: 'A proposal can’t stay open past the date it’s for.', path: ['validUntil'] })
export type CreatePacketInput = z.infer<typeof createPacketInputSchema>

export const extendPacketInputSchema = z.object({ validUntil: calendarDaySchema }).strict()
export type ExtendPacketInput = z.infer<typeof extendPacketInputSchema>

export const setCountersignerInputSchema = z.object({ countersignerId: z.string().uuid() }).strict()
export type SetCountersignerInput = z.infer<typeof setCountersignerInputSchema>

/** What the couple picks (docs/sales.spec.md, "Choosing and signing"): one package, if any are offered, and any add-ons. */
export const packetChoiceInputSchema = z
  .object({
    packageLineId: z.string().uuid().nullable(),
    addOnLineIds: z.array(z.string().uuid()).max(100),
  })
  .strict()
export type PacketChoiceInput = z.infer<typeof packetChoiceInputSchema>

/**
 * An invoice for extras on a booked event (docs/sales.spec.md, "After
 * signing"): what's added, always included, and when it's paid. No signature;
 * the people named are emailed it.
 */
export const createInvoiceInputSchema = z
  .object({
    lines: z
      .array(packetLineInputSchema)
      .min(1, 'Add at least one thing.')
      .max(100, 'That’s too many lines.')
      .refine((lines) => lines.every((l) => l.group === 'included'), 'Everything on an invoice is included; there’s nothing to choose.'),
    schedule: z.array(scheduleEntryInputSchema).min(1).max(MAX_SCHEDULE_PAYMENTS),
    notifyUserIds: z.array(z.string().uuid()).max(6),
  })
  .strict()
export type CreateInvoiceInput = z.infer<typeof createInvoiceInputSchema>

/**
 * A change to a booked event's agreement (docs/sales.spec.md, "After
 * signing"): a proposal again, signed by the same people, naming the
 * agreement it changes.
 */
export const createAmendmentInputSchema = z
  .object({ ...proposalFields, amendsPacketId: z.string().uuid() })
  .strict()
  .refine((p) => (p.startsAt === null) === (p.endsAt === null), { message: 'Give both times, or neither.', path: ['endsAt'] })
  .refine((p) => p.validUntil <= p.date, { message: 'A change can’t stay open past the date it’s for.', path: ['validUntil'] })
export type CreateAmendmentInput = z.infer<typeof createAmendmentInputSchema>
